<script setup lang="ts">
  import { SHOW_ALL_FILTER } from '@/utils/constants/meals'
  import { cva } from 'class-variance-authority'

  const props = withDefaults(
    defineProps<{
      filters: (string | number)[]
      showAllOption: boolean
      buttonStyle: 'primary' | 'secondary'
      layoutMode: 'scroll' | 'wrap'
    }>(),
    {
      showAllOption: true,
      buttonStyle: 'primary',
      layoutMode: 'scroll'
    }
  )

  const stringFilters = computed<string[]>(() => {
    const mappedFilters = props.filters.map(String)

    if (!props.showAllOption) {
      return mappedFilters
    }

    return [SHOW_ALL_FILTER, ...mappedFilters]
  })

  const selected = defineModel<string>('selected', { default: SHOW_ALL_FILTER })

  const rootCva = computed(() =>
    cva('flex gap-2 text-body-2 md:gap-4 md:text-subtitle-1 overflow-x-auto flex-nowrap', {
      variants: {
        layoutMode: {
          scroll: 'min-w-0 overflow-y-hidden',
          wrap: 'w-full sm:flex-wrap sm:justify-center'
        }
      }
    })({
      layoutMode: props.layoutMode
    })
  )

  const buttonCva = computed(() =>
    cva(
      'whitespace-nowrap rounded-full border border-border bg-white px-4 py-1.5 font-medium transition-colors md:px-6 md:py-2',
      {
        variants: {
          style: {
            primary:
              'hover:border-bt-green hover:bg-bt-green data-[state=checked]:border-bt-green data-[state=checked]:bg-bt-green',
            secondary:
              'hover:border-bt-blue-dark hover:bg-bt-blue-dark hover:text-light-info data-[state=checked]:border-bt-blue-dark data-[state=checked]:bg-bt-blue-dark data-[state=checked]:text-light-info'
          }
        }
      }
    )({
      style: props.buttonStyle
    })
  )
</script>

<template>
  <div class="my-6 flex w-full min-w-0 md:mx-auto md:justify-center lg:mb-14 lg:mt-12">
    <RadioGroupRoot
      v-model="selected"
      type="single"
      :class="rootCva"
    >
      <RadioGroupItem
        v-for="filter in stringFilters"
        :key="filter"
        :value="filter"
        :class="buttonCva"
      >
        {{ $t('meals.categories.' + filter) }}
      </RadioGroupItem>
    </RadioGroupRoot>
  </div>
</template>
